import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { PartnerService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export const organizationsQueryKeys = {
  all: () => ['organizations'] as const,
  lists: () => ['organizations', 'list'] as const,
  list: (filters?: { type?: PartnerService.OrganizationType; active?: boolean }) =>
    ['organizations', 'list', filters] as const,
  details: () => ['organizations', 'detail'] as const,
  detail: (id: string) => ['organizations', 'detail', id] as const,
  branding: (id: string) => ['organizations', 'branding', id] as const,
}

const fetchOrganizations = async (
  type?: PartnerService.OrganizationType,
  active?: boolean
): Promise<PartnerService.Organization[]> => {
  const queryFilters = (type || active !== undefined) ? { type, active } : undefined
  const response = await apis.partnerService.organizationsGet(queryFilters)
  return response.data.data || []
}

export const useOrganizations = (filters?: {
  type?: PartnerService.OrganizationType
  active?: boolean
}) => {
  const query = useQuery({
    queryKey: organizationsQueryKeys.list(filters),
    queryFn: () => fetchOrganizations(filters?.type, filters?.active),
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  const organizations = query.data ?? []

  // Create a map for quick organization lookup by ID
  const idToNameMap = useMemo(() => {
    const map = new Map<string, string>()
    organizations.forEach(org => {
      map.set(org.id, org.name)
    })
    return map
  }, [organizations])

  return {
    organizations,
    idToNameMap,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export const useInvalidateOrganizations = () => {
  const queryClient = useQueryClient()

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: organizationsQueryKeys.all() })
  }

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: organizationsQueryKeys.lists() })
  }

  return {
    invalidateAll,
    invalidateLists,
  }
}
